import { Button } from '../components/ui/Button'
import {
  mt5Credibility,
  sharedAccountFacts,
  strategies,
} from '../content/site'

export function HomePage() {
  return (
    <>
      <section className="pp-hero" aria-labelledby="hero-heading">
        <div className="pp-container pp-hero__grid">
          <div>
            <span className="pp-badge">Built on trusted MT5 infrastructure</span>
            <h1 id="hero-heading">AI-powered prediction trading. Same MT5 account.</h1>
            <p className="pp-hero__lead">
              Perfect Predict is an AI-powered prediction-first trading experience
              built on trusted MT5 infrastructure. One trading account. Two ways
              to trade — Portal simplicity or full MetaTrader 5 control.
            </p>
            <div className="pp-hero__actions">
              <Button to="/register" variant="gold">
                Open Perfect Predict
              </Button>
              <Button to="/mt5-infrastructure" variant="ghost">
                Explore MT5 Infrastructure
              </Button>
            </div>
            <div className="pp-hero__proof" aria-label="Credibility markers">
              <span>Powered by MT5</span>
              <span>Executed on MT5</span>
              <span>MT5 Verified</span>
            </div>
          </div>

          <aside className="pp-hero-panel" aria-label="Two ways to trade">
            <div className="pp-hero-panel__title">Core promise</div>
            <div className="pp-hero-panel__headline">One Trading Account. Two Ways to Trade.</div>
            <ol className="pp-hero-panel__list">
              <li>
                <span className="pp-hero-panel__num">1</span>
                <div>
                  <strong>Perfect Predict Portal</strong>
                  <span>Beginner-friendly, AI-assisted prediction experience.</span>
                </div>
              </li>
              <li>
                <span className="pp-hero-panel__num">2</span>
                <div>
                  <strong>MetaTrader 5</strong>
                  <span>Desktop and mobile for professional manual trading and EAs.</span>
                </div>
              </li>
            </ol>
          </aside>
        </div>
      </section>

      <section className="pp-section" aria-labelledby="dual-heading">
        <div className="pp-container">
          <p className="pp-section__eyebrow">Shared account model</p>
          <h2 id="dual-heading" className="pp-section__title">
            One Trading Account. Two Ways to Trade.
          </h2>
          <p className="pp-section__lead">
            Perfect Predict does not replace MetaTrader 5. It gives you a modern
            prediction experience on the same MT5 login, balance, equity, and history.
          </p>

          <div className="pp-dual" style={{ marginTop: '2.5rem' }}>
            <article className="pp-dual-card pp-dual-card--portal">
              <span className="pp-badge">Experience 1</span>
              <h3>Perfect Predict Portal</h3>
              <p>
                Designed for beginners, investors, and anyone who wants simple,
                AI-assisted prediction flow — market, strategy, BUY or SELL, stake.
              </p>
              <ul>
                <li>Simple prediction-first interface</li>
                <li>Clear strategies: Safe, Balanced, Aggressive</li>
                <li>Same MT5 account underneath</li>
              </ul>
              <Button to="/how-it-works">How the Portal works</Button>
            </article>

            <article className="pp-dual-card pp-dual-card--mt5">
              <span className="pp-badge">Experience 2</span>
              <h3>MetaTrader 5</h3>
              <p>
                Designed for professional traders who need charts, Expert Advisors,
                and full manual control on desktop or mobile.
              </p>
              <ul>
                <li>Advanced charting and order management</li>
                <li>Expert Advisors and professional tooling</li>
                <li>Identical MT5 login and balances</li>
              </ul>
              <Button to="/mt5-infrastructure" variant="secondary">
                Download MT5 path
              </Button>
            </article>
          </div>

          <div className="pp-shared" aria-label="Shared account data">
            {sharedAccountFacts.map((fact) => (
              <span key={fact}>{fact}</span>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section pp-section--alt" aria-labelledby="strategies-heading">
        <div className="pp-container">
          <p className="pp-section__eyebrow">Prediction Strategies</p>
          <h2 id="strategies-heading" className="pp-section__title">
            Choose your prediction posture
          </h2>
          <p className="pp-section__lead">
            Strategies shape how you participate in Perfect Predict. They are not
            account tiers — they describe conviction and exposure style on your
            existing MT5 trading account.
          </p>

          <div className="pp-grid-3" style={{ marginTop: '2.5rem' }}>
            {strategies.map((strategy) => (
              <article
                key={strategy.id}
                className={`pp-card pp-strategy-card--${strategy.id}`}
              >
                <div className="pp-card__icon" aria-hidden="true">
                  {strategy.id === 'safe' ? 'S' : strategy.id === 'balanced' ? 'B' : 'A'}
                </div>
                <h3>{strategy.name}</h3>
                <p>{strategy.tone}</p>
                <div className="pp-strategy-card__meta">
                  <span>{strategy.badge}</span>
                  {strategy.points.map((point) => (
                    <span key={point}>{point}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="pp-section" aria-labelledby="mt5-heading">
        <div className="pp-container pp-mt5">
          <div>
            <p className="pp-section__eyebrow">MT5 credibility</p>
            <h2 id="mt5-heading" className="pp-section__title">
              Proudly powered by MetaTrader 5
            </h2>
            <p className="pp-section__lead">
              Perfect Predict never hides MT5. Predictions, balances, positions, and
              history remain grounded in your MT5 trading account — whether you open
              the Portal or trade in MetaTrader itself.
            </p>
            <div className="pp-hero__actions" style={{ marginTop: '1.75rem' }}>
              <Button to="/mt5-infrastructure">MT5 Infrastructure</Button>
              <Button to="/register" variant="secondary">
                Create account
              </Button>
            </div>
          </div>

          <div className="pp-mt5__panel">
            <h3>MT5 is the source of truth</h3>
            <p>
              Portal and MetaTrader are two experiences over one account. No second
              wallet. No duplicate balance. No parallel ledger.
            </p>
            <div className="pp-mt5__tags" aria-label="MT5 trust markers">
              {mt5Credibility.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
